import {AfterViewInit, Component, computed, ContentChild, ElementRef, input, output, signal, TemplateRef, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {AppControlErrorComponent} from '@shared/components/forms/app-control-error/app-control-error.component';
import {BaseAppControlValueAccessorComponent} from '@shared/components/forms/app-control-value-accessor/base-app-control-value-accessor.component';
import {NgSelectModule} from '@ng-select/ng-select';
import {CommonModule} from '@angular/common';
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";

export type IconPosition = 'left' | 'right';

@Component({
    selector: 'app-select',
    templateUrl: './app-select.component.html',
    styleUrls: ['./app-select.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, TranslateModule, AppControlErrorComponent, NgSelectModule, CommonModule, NgbTooltip]
})
export class AppSelectComponent extends BaseAppControlValueAccessorComponent implements AfterViewInit {
    @ViewChild('selectElement', {static: false}) selectElement?: ElementRef;

    // Templates customizados
    @ContentChild('optionTemplate', {static: false}) customOptionTemplate: TemplateRef<any> | null = null;
    @ContentChild('labelTemplate', {static: false}) customLabelTemplate: TemplateRef<any> | null = null;
    @ContentChild('headerTemplate', {static: false}) customHeaderTemplate: TemplateRef<any> | null = null;
    @ContentChild('footerTemplate', {static: false}) customFooterTemplate: TemplateRef<any> | null = null;
    @ContentChild('groupTemplate', {static: false}) customGroupTemplate: TemplateRef<any> | null = null;
    @ContentChild('loadingTemplate', {static: false}) customLoadingTemplate: TemplateRef<any> | null = null;
    @ContentChild('notFoundTemplate', {static: false}) customNotFoundTemplate: TemplateRef<any> | null = null;
    @ContentChild('typeToSearchTemplate', {static: false}) customTypeToSearchTemplate: TemplateRef<any> | null = null;

    // Inputs básicos do componente
    showErrorDescription = input<boolean>(true);
    containerStyles = input<string>('');
    groupIcon = input<string>('');
    groupIconPosition = input<IconPosition>('left');
    label = input<string>('');
    labelTooltip = input<string>('');
    placeholder = input<string>('');
    hint = input<string>('');
    hintIcon = input<string>('');
    customErrors = input<Record<string, string>>({});
    readonly = input<boolean>(false);

    // Configuração de dados - DINÂMICA como ng-select
    items = input<any[]>([]);
    bindLabel = input<string>('');
    bindValue = input<string | null>(null);
    groupBy = input<string | null>(null);

    // Configurações específicas do ng-select
    multiple = input<boolean>(false);
    clearable = input<boolean>(true);
    searchable = input<boolean>(true);
    closeOnSelect = input<boolean>(true);
    selectOnTab = input<boolean>(false);
    openOnEnter = input<boolean>(true);
    dropdownPosition = input<'bottom' | 'top' | 'auto'>('bottom');
    appendTo = input<string>('');
    maxSelectedItems = input<number | null>(null);
    hideSelected = input<boolean>(false);
    loadingText = input<string>('common.label.loading');
    notFoundText = input<string>('common.label.not_found');
    typeToSearchText = input<string>('common.label.type_to_search');
    addTagText = input<string>('common.label.add');
    clearAllText = input<string>('common.label.clear_all');

    // Limite de itens por grupo
    maxItemsPerGroup = input<number | null>(null);

    // Configurações adicionais
    compareWith = input<((o1: any, o2: any) => boolean) | null>(null);

    // Recursos avançados
    addTag = input<boolean | ((term: string) => any)>(false);
    editableSearchTerm = input<boolean>(false);
    selectableGroup = input<boolean>(false);
    selectableGroupAsModel = input<boolean>(true);
    minTermLength = input<number>(0);
    typeahead = input<any>(null);
    virtualScroll = input<boolean>(false);

    // Outputs
    valueChange = output<any>();

    // Estado interno do componente
    private isFocused = signal<boolean>(false);
    private searchTerm = signal<string>('');

    // Computeds para verificar se há templates customizados
    hasCustomOptionTemplate = computed(() => !!this.customOptionTemplate);
    hasCustomLabelTemplate = computed(() => !!this.customLabelTemplate);
    hasCustomHeaderTemplate = computed(() => !!this.customHeaderTemplate);
    hasCustomFooterTemplate = computed(() => !!this.customFooterTemplate);
    hasCustomGroupTemplate = computed(() => !!this.customGroupTemplate);
    hasCustomLoadingTemplate = computed(() => !!this.customLoadingTemplate);
    hasCustomNotFoundTemplate = computed(() => !!this.customNotFoundTemplate);
    hasCustomTypeToSearchTemplate = computed(() => !!this.customTypeToSearchTemplate);

    containerClasses = computed(() => {
        const classes = ['form-group'];
        if (this.containerStyles()) classes.push(this.containerStyles());
        if (this.isFocused()) classes.push('focused');
        return classes.join(' ');
    });

    // Computed para detecção automática de propriedades
    computedBindLabel: any = computed(() => {
        const bindLabel = this.bindLabel();
        if (bindLabel) return bindLabel;

        // Auto-detecção baseada no primeiro item
        const items = this.items();
        if (!items.length) return null;

        const firstItem = items[0];
        if (typeof firstItem === 'string' || typeof firstItem === 'number') {
            return null; // Para primitivos, ng-select usa o próprio valor
        }

        if (typeof firstItem === 'object' && firstItem !== null) {
            // Tenta campos comuns
            const commonFields = ['name', 'label', 'title', 'text', 'description', 'displayName', 'labelKey'];
            for (const field of commonFields) {
                if (firstItem[field] !== undefined) {
                    return field;
                }
            }
        }

        return null;
    });

    computedBindValue: any = computed(() => {
        const bindValue = this.bindValue();
        if (bindValue) return bindValue;

        // Para primitivos ou quando não especificado, usa o item inteiro
        return null;
    });

    // Computed para placeholder
    computedPlaceholder = computed(() => {
        const placeholder = this.placeholder();
        if (placeholder) {
            return placeholder;
        }
        return '';
    });

    // Computed para textos traduzidos
    computedLoadingText = computed(() => this.loadingText());
    computedNotFoundText = computed(() => this.notFoundText());
    computedTypeToSearchText = computed(() => this.typeToSearchText());
    computedAddTagText = computed(() => this.addTagText());
    computedClearAllText = computed(() => this.clearAllText());

    constructor() {
        super();
    }

    ngAfterViewInit(): void {
        this.setupControlListener();
    }

    // Event handlers específicos do ng-select
    onFocus(): void {
        this.isFocused.set(true);
        this.markAsTouched();
    }

    onBlur(): void {
        this.isFocused.set(false);
    }

    onOpen(): void {
        // Callback quando dropdown abre
    }

    onClose(): void {
        // Callback quando dropdown fecha
    }

    onClear(): void {
        this.valueChange.emit(this.getValue());
    }

    onValueChange(item: any): void {
        const newValue = this.getValue();

        // Valida se pode selecionar baseado no limite por grupo
        if (this.multiple() && this.maxItemsPerGroup() && this.groupBy()) {
            const validatedValue = this.validateGroupLimits(newValue);

            if (validatedValue.length !== newValue.length) {

                // Força o valor validado no ng-select
                setTimeout(() => {
                    this.setValue(validatedValue);
                    // Força a atualização visual
                    this.selectElement?.nativeElement?.writeValue(validatedValue);
                }, 0);

                this.valueChange.emit(validatedValue);
                return;
            }
        }

        this.valueChange.emit(newValue);
    }

    onAdd(item: any): void {
    }

    onRemove(item: any): void {
    }

    onScroll(event: any): void {
        // Callback para scroll (útil para virtual scroll)
    }

    onScrollToEnd(): void {
        // Callback quando chega no fim da lista
    }

    onSearch(event: { term: string; items: any[] }): void {
        this.searchTerm.set(event.term || '');
    }

    // Métodos utilitários
    focus(): void {
        this.selectElement?.nativeElement?.focus();
    }

    blur(): void {
        this.selectElement?.nativeElement?.blur();
    }

    open(): void {
        this.selectElement?.nativeElement?.open();
    }

    close(): void {
        this.selectElement?.nativeElement?.close();
    }

    clear(): void {
        this.setValue(this.multiple() ? [] : null);
        this.focus();
    }

    // Getter/setter para compatibilidade com template
    get currentValue(): any {
        return this.getValue();
    }

    set currentValue(value: any) {
        this.setValue(value);
    }

    // Método para acessibilidade
    getAriaDescribedBy(): string {
        const describedBy: string[] = [];
        if (this.hint()) {
            describedBy.push(this.inputId + '_hint');
        }
        return describedBy.join(' ') || '';
    }

    // Computed para agrupar itens - corrigido tipos
    computedGroupBy: any = computed(() => {
        const groupBy = this.groupBy();
        if (!groupBy) return undefined;
        return this.groupByFn;
    });

    // Computed para comparação - corrigido tipos
    computedCompareWith: any = computed(() => {
        const compareWith = this.compareWith();
        if (!compareWith) return undefined;
        return this.compareWithFn;
    });

    // Função para agrupar itens
    groupByFn = (item: any): any => {
        const groupBy = this.groupBy();
        if (!groupBy) return null;

        if (typeof item === 'object' && item !== null) {
            // Suporta notação de ponto (ex: "module.name")
            return this.getNestedProperty(item, groupBy);
        }

        return null;
    };

    // Função de comparação customizada
    compareWithFn = (item1: any, item2: any): boolean => {
        const customCompare = this.compareWith();
        if (customCompare) {
            return customCompare(item1, item2);
        }

        // Comparação padrão
        return item1 === item2;
    };

    // Métodos para controle de seleção múltipla
    selectAll(): void {
        if (!this.multiple()) return;

        const filteredItems = this.getFilteredItems();
        const bindValue = this.computedBindValue();

        if (bindValue) {
            const allValues = filteredItems.map(item => item[bindValue]);
            this.setValue(allValues);
        } else {
            this.setValue([...filteredItems]);
        }

        this.valueChange.emit(this.getValue());
    }

    deselectAll(): void {
        if (!this.multiple()) return;

        this.setValue([]);
        this.valueChange.emit(this.getValue());
    }

    areAllSelected(): boolean {
        if (!this.multiple()) return false;

        const currentValue = this.getValue() || [];
        const filteredItems = this.getFilteredItems();

        return currentValue.length === filteredItems.length && filteredItems.length > 0;
    }

    isSomeSelected(): boolean {
        if (!this.multiple()) return false;

        const currentValue = this.getValue() || [];
        const filteredItems = this.getFilteredItems();

        return currentValue.length > 0 && currentValue.length < filteredItems.length;
    }

    toggleSelectAll(): void {
        if (this.areAllSelected()) {
            this.deselectAll();
        } else {
            this.selectAll();
        }
    }

    // Validação do limite por grupo - busca objetos originais pelos IDs
    private validateGroupLimits(selectedItems: any[]): any[] {
        const maxPerGroup = this.maxItemsPerGroup();
        const groupBy = this.groupBy();
        const bindValue = this.computedBindValue();

        if (!maxPerGroup || !groupBy || !Array.isArray(selectedItems)) {
            return selectedItems;
        }

        // Se está usando bindValue, precisa buscar os objetos originais
        let itemsToValidate = selectedItems;
        if (bindValue) {
            const allItems = this.items();
            itemsToValidate = selectedItems.map(selectedId => {
                return allItems.find(item => item[bindValue] === selectedId);
            }).filter(item => item !== undefined);
        }

        const groupCounts = new Map<string, number>();
        const validItems: any[] = [];

        itemsToValidate.forEach((item, index) => {
            const groupValue = this.getGroupValue(item);

            const currentCount = groupCounts.get(groupValue) || 0;

            if (currentCount < maxPerGroup) {
                // Se está usando bindValue, retorna o ID, senão retorna o objeto
                const itemToAdd = bindValue ? item[bindValue] : item;
                validItems.push(itemToAdd);
                groupCounts.set(groupValue, currentCount + 1);
            }
        });

        return validItems;
    }

    private getGroupValue(item: any): string {
        const groupBy = this.groupBy();
        if (!groupBy) return '';

        if (typeof item === 'object' && item !== null) {
            // Suporta notação de ponto (ex: "module.name")
            const value = this.getNestedProperty(item, groupBy);
            return String(value || 'sem-grupo');
        }

        return String(item || 'sem-valor');
    }

    private getNestedProperty(obj: any, path: string): any {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : null;
        }, obj);
    }

    // Método para obter itens filtrados baseado no termo de busca
    private getFilteredItems(): any[] {
        const allItems = this.items();
        const term = this.searchTerm().toLowerCase().trim();

        // Se não há termo de busca, retorna todos os itens
        if (!term) {
            return allItems;
        }

        const bindLabel = this.computedBindLabel();

        // Filtra os itens baseado no termo de busca
        return allItems.filter(item => {
            if (typeof item === 'string' || typeof item === 'number') {
                return String(item).toLowerCase().includes(term);
            }

            if (typeof item === 'object' && item !== null && bindLabel) {
                const label = this.getNestedProperty(item, bindLabel);
                return String(label || '').toLowerCase().includes(term);
            }

            return false;
        });
    }
}
